import { BaseDialog } from '@/components/BaseDialog'
import { SectionCard } from '@/components/SectionCard'
import { useGetFloor } from '@/features/floormaps/hooks/useFloor'
import { MapStage } from '@/features/map/components/MapState'
import { Dot } from '@/features/map/types/shape'
import { mapLocationToDot } from '@/features/map/utils/mapMappers'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useTranslation } from 'react-i18next'
import { ItemLocation } from '../types/item-location'
import { ItemLocationTimeline } from './ItemLocationTimeline'

export function ItemLocationMapDialog({
  open,
  onOpenChange,
  selectedLocation,
  itemLocations = [],
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  selectedLocation: ItemLocation | null
  itemLocations: ItemLocation[]
}) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()

  const { data: floor, isFetching: isFetchingFloor } = useGetFloor(
    selectedLocation?.buildingId ?? '',
    selectedLocation?.floorId ?? '',
    open && !!selectedLocation?.buildingId && !!selectedLocation?.floorId,
  )

  const filteredLocationDetails = itemLocations.filter(
    x =>
      x.buildingId === selectedLocation?.buildingId &&
      x.floorId === selectedLocation?.floorId &&
      x.gatewayId === selectedLocation?.gatewayId,
  )

  const filteredLocations = itemLocations.filter(
    x => x.buildingId === selectedLocation?.buildingId && x.floorId === selectedLocation?.floorId,
  )

  const groupedByGateway = Object.values(
    filteredLocations.reduce(
      (acc, location) => {
        const key = location.gatewayId

        if (!acc[key]) {
          acc[key] = []
        }

        acc[key].push(location)
        return acc
      },
      {} as Record<string, ItemLocation[]>,
    ),
  )

  const dots = groupedByGateway
    .map(group => {
      const latest = [...group].sort((a, b) => b.recordedAt.localeCompare(a.recordedAt))[0]

      const isSelectedGateway = latest.gatewayId === selectedLocation?.gatewayId

      const label = isSelectedGateway
        ? group.length > 1
          ? `${latest.gatewayName} (${group.length})`
          : formatDateTime(latest.recordedAt, {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })
        : formatDateTime(latest.recordedAt, {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })

      return mapLocationToDot(latest, label, latest.gatewayId === selectedLocation?.gatewayId)
    })
    .filter((dot): dot is Dot => dot !== null)

  return (
    <BaseDialog open={open} onOpenChange={onOpenChange} dialogContentClass="sm:max-w-6xl">
      <div className="grid grid-cols-12 h-full ">
        {/* LEFT MAP */}
        <div className="col-span-12 overflow-auto p-4 md:col-span-8 md:border-r">
          <div className="flex justify-center">
            <MapStage imageId={floor?.imageId ?? ''} width={600} height={400} dots={dots} mode={'view'} />
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-span-12 md:col-span-4 overflow-y-auto p-4">
          <SectionCard title={t('scans.section.locationHistory')} headerClass="pb-0">
            <ItemLocationTimeline itemLocations={filteredLocationDetails}></ItemLocationTimeline>
          </SectionCard>
        </div>
      </div>
    </BaseDialog>
  )
}
